import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { IEirSalesSaveAscertainPayload } from "@modules/eirSalesFacade/interfaces/payloads/IEirSalesSaveAscertainPayload";
import { IEirSalesAscertain } from "@modules/eirSalesFacade/interfaces/models/IEirSalesAscertain";
import { mapEirSalesSaveAscertainPayload } from "@modules/eirSalesFacade/mappers/mapEirSalesSaveAscertainPayload";
import { KeyedMutator } from "swr";

interface IBroadbandUanFormData {
    uan: string;
    cancelHomePhone: boolean;
}

interface IUseBroadbandUanFormParams {
    ascertain?: IEirSalesAscertain;
    isLoading: boolean;
    error?: { status?: number } | null;
    mutateAscertain: KeyedMutator<IEirSalesAscertain>;
    saveAscertain(
        payload: IEirSalesSaveAscertainPayload,
        successCallback?: (ascertain: IEirSalesAscertain) => void
    ): Promise<void>;
    onAnswerChange?(): void;
    onValidationChange?(hasErrors: boolean): void;
}

export const useBroadbandUanForm = (params: IUseBroadbandUanFormParams) => {
    const { ascertain, saveAscertain, mutateAscertain, onAnswerChange, error, onValidationChange } = params;

    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showValidationError, setShowValidationError] = useState(false);
    const [lastSubmittedData, setLastSubmittedData] = useState<IBroadbandUanFormData | null>(null);
    const [showFormInsteadOfError, setShowFormInsteadOfError] = useState(false);

    const uan = ascertain?.broadbandAccountNumber;
    const hasUan = Boolean(uan);

    const hasQaFailedError = error?.status === 500;
    const hasOtherError = Boolean(error) && error?.status !== 500;

    const accordionExpanded = useMemo(() => {
        if (hasQaFailedError || hasOtherError) {
            return true;
        }
        if (isEditing) {
            return true;
        }
        if (isAccordionOpen) {
            return true;
        }

        return !hasUan;
    }, [hasQaFailedError, hasOtherError, isEditing, isAccordionOpen, hasUan]);

    const { control, handleSubmit, errors, reset, trigger } = useForm<IBroadbandUanFormData>({
        defaultValues: {
            uan: uan ?? "",
            cancelHomePhone: hasUan,
        },
    });

    const openAccordion = useCallback(() => setIsAccordionOpen(true), []);
    const closeAccordion = useCallback(() => setIsAccordionOpen(false), []);
    const toggleAccordion = useCallback(() => setIsAccordionOpen((prev) => !prev), []);

    const triggerValidation = useCallback(() => {
        trigger();
        openAccordion();
        setIsAccordionOpen(true);
        setShowValidationError(!hasUan);
    }, [trigger, openAccordion, hasUan]);

    const resetUanSection = useCallback(
        (formData: IBroadbandUanFormData) => {
            closeAccordion();
            setIsEditing(false);
            setIsAccordionOpen(false);
            setShowValidationError(false);
            setLastSubmittedData(null);
            setShowFormInsteadOfError(false);
            reset({ uan: formData.uan, cancelHomePhone: true });

            if (onAnswerChange) {
                onAnswerChange();
            }
        },
        [closeAccordion, reset, onAnswerChange]
    );

    const onSubmit = useCallback(
        async (formData: IBroadbandUanFormData) => {
            // If the UAN has not changed, do not make the API call
            if (formData.uan === uan) {
                resetUanSection(formData);

                return;
            }

            setLastSubmittedData(formData);
            const payload = mapEirSalesSaveAscertainPayload(true, formData.uan);
            await saveAscertain(payload, (savedAscertain) => {
                mutateAscertain(savedAscertain, false);
                resetUanSection(formData);
            });
        },
        [saveAscertain, mutateAscertain, resetUanSection, uan]
    );

    const handleRetry = useCallback(() => {
        if (lastSubmittedData) {
            onSubmit(lastSubmittedData);
        }
    }, [lastSubmittedData, onSubmit]);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setShowFormInsteadOfError(false);

        if (hasUan) {
            closeAccordion();
            setIsAccordionOpen(false);
        } else {
            openAccordion();
            setIsAccordionOpen(true);
        }

        reset({ uan: uan ?? "", cancelHomePhone: hasUan });
    }, [hasUan, uan, closeAccordion, openAccordion, reset]);

    const handleEdit = useCallback(() => {
        setIsEditing(true);
        openAccordion();
        setIsAccordionOpen(true);
        reset({ uan: uan ?? "", cancelHomePhone: false });
    }, [openAccordion, uan, reset]);

    useEffect(() => {
        if ((hasQaFailedError || hasOtherError) && ascertain?.broadbandAccountNumber) {
            const updatedAscertain: IEirSalesAscertain = {
                ...ascertain,
                broadbandAccountNumber: undefined,
            };
            mutateAscertain(updatedAscertain, false);
            reset({ uan: "", cancelHomePhone: false });
        }
    }, [hasQaFailedError, hasOtherError, ascertain, mutateAscertain, reset]);

    // Reset showFormInsteadOfError when a new error occurs
    useEffect(() => {
        if (errors) {
            setShowFormInsteadOfError(false);
        }
    }, [errors]);

    const handleChangeUan = useCallback(() => {
        setIsEditing(true);
        setShowFormInsteadOfError(true);
        openAccordion();
        setIsAccordionOpen(true);
    }, [openAccordion]);

    const shouldShowErrorContent = useMemo(() => {
        if (showFormInsteadOfError) {
            return false;
        }

        return hasQaFailedError || hasOtherError;
    }, [showFormInsteadOfError, hasQaFailedError, hasOtherError]);

    const getHasValidationErrors = useMemo(
        () => Boolean(errors.uan ?? errors.cancelHomePhone ?? (showValidationError && !hasUan)),
        [errors.uan, errors.cancelHomePhone, showValidationError, hasUan]
    );

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(getHasValidationErrors);
        }
    }, [getHasValidationErrors, onValidationChange]);

    return {
        uan,
        hasUan,
        isEditing,
        accordionExpanded,
        control,
        errors,
        handleSubmit: handleSubmit(onSubmit),
        toggleAccordion,
        handleCancel,
        handleEdit,
        triggerValidation,
        showValidationError,
        hasQaFailedError,
        hasOtherError,
        shouldShowErrorContent,
        handleRetry,
        handleChangeUan,
        getHasValidationErrors,
    };
};
